import React from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';

const fmtFull = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);

const SYSTEM_COLORS = {
  'FERS': { bg: 'bg-[#1B2A4A]', text: 'text-white' },
  'FERS-RAE': { bg: 'bg-[#2D5F8A]', text: 'text-white' },
  'FERS-FRAE': { bg: 'bg-[#3B8C6E]', text: 'text-white' },
  'CSRS': { bg: 'bg-[#6B3B8C]', text: 'text-white' },
  'BRS': { bg: 'bg-[#8B6B3B]', text: 'text-white' },
};

export function ParticipantBadge({ retirementSystem, name }) {
  const sys = retirementSystem || 'FERS';
  const colors = SYSTEM_COLORS[sys] || SYSTEM_COLORS['FERS'];
  return (
    <div className="flex items-center gap-3" data-testid="participant-badge">
      <span className={`inline-flex items-center px-2.5 py-1 text-[11px] font-bold tracking-wider uppercase ${colors.bg} ${colors.text}`} style={{ fontFamily: 'IBM Plex Mono, monospace' }} data-testid="retirement-system-badge">
        {sys}
      </span>
      {name && <span className="text-sm font-medium text-[#1B2A4A]" data-testid="participant-name">{name}</span>}
    </div>
  );
}

export function TotalBalanceCard({ total, monthlyChange, monthlyChangePct, loading }) {
  if (loading) return <BalanceSkeleton />;
  const isPositive = monthlyChange > 0;
  const isNegative = monthlyChange < 0;
  const ChangeIcon = isPositive ? ArrowUpRight : isNegative ? ArrowDownRight : Minus;
  const changeColor = isPositive ? 'text-emerald-600' : isNegative ? 'text-red-600' : 'text-[#6B7280]';

  return (
    <div className="panel p-5 panel-hover" data-testid="total-balance-card">
      <span className="overline">Total Retirement Balance</span>
      <div className="mt-2 font-mono text-3xl lg:text-4xl font-bold text-[#1B2A4A] tracking-tight tabular-nums" data-testid="total-balance-value">
        {fmt(total)}
      </div>
      <div className="mt-2 flex items-center gap-1.5">
        <ChangeIcon size={14} className={changeColor} />
        <span className={`text-xs font-mono font-medium ${changeColor}`} data-testid="monthly-change">
          {isPositive ? '+' : ''}{fmtFull(monthlyChange)}
        </span>
        <span className={`text-xs font-mono ${changeColor}`} data-testid="monthly-change-pct">
          ({isPositive ? '+' : ''}{monthlyChangePct?.toFixed(2)}%)
        </span>
        <span className="text-[10px] text-[#9CA3AF] ml-1">vs prior month</span>
      </div>
    </div>
  );
}

export function TraditionalBalanceCard({ amount, loading }) {
  if (loading) return <BalanceSkeleton />;
  return (
    <div className="panel p-5 panel-hover" data-testid="traditional-balance-card">
      <div className="flex items-center justify-between">
        <span className="overline">Traditional</span>
        <span className="text-[10px] font-medium text-[#1B2A4A] bg-[#F0F1F3] px-2 py-0.5 rounded-sm" style={{ fontFamily: 'IBM Plex Mono' }}>PRE-TAX</span>
      </div>
      <div className="mt-2 font-mono text-2xl lg:text-3xl font-semibold text-[#1B2A4A] tracking-tight tabular-nums" data-testid="traditional-balance-value">
        {fmt(amount)}
      </div>
      <p className="mt-1.5 text-[10px] text-[#9CA3AF] leading-relaxed" data-testid="traditional-note">
        Includes all agency contributions (26 U.S.C. § 402A)
      </p>
    </div>
  );
}

export function RothBalanceCard({ amount, loading }) {
  if (loading) return <BalanceSkeleton />;
  return (
    <div className="panel p-5 panel-hover" data-testid="roth-balance-card">
      <div className="flex items-center justify-between">
        <span className="overline">Roth</span>
        <span className="text-[10px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-sm" style={{ fontFamily: 'IBM Plex Mono' }}>POST-TAX</span>
      </div>
      <div className="mt-2 font-mono text-2xl lg:text-3xl font-semibold text-[#1B2A4A] tracking-tight tabular-nums" data-testid="roth-balance-value">
        {fmt(amount)}
      </div>
      <p className="mt-1.5 text-[10px] text-[#9CA3AF] leading-relaxed" data-testid="roth-note">
        Tax-free qualified withdrawals
      </p>
    </div>
  );
}

export function IncomeProjectionCard({ projection, loading }) {
  if (loading) return <BalanceSkeleton large />;
  if (!projection) return null;
  const { tsp_monthly, fers_pension, social_security, total_monthly } = projection;

  return (
    <div className="panel p-5 panel-hover" data-testid="income-projection-card">
      <span className="overline">Est. Monthly Retirement Income</span>
      <div className="mt-2 font-mono text-2xl lg:text-3xl font-bold text-[#1B2A4A] tracking-tight tabular-nums" data-testid="total-monthly-income">
        {fmt(total_monthly)}
        <span className="text-sm font-normal text-[#6B7280] ml-1">/mo</span>
      </div>
      <div className="mt-3 space-y-1.5">
        <IncomeStream label="Retirement Drawdown" amount={tsp_monthly} color="#1B2A4A" />
        <IncomeStream label="FERS Pension" amount={fers_pension} color="#2D5F8A" />
        <IncomeStream label="Social Security" amount={social_security} color="#3B8C6E" />
      </div>
    </div>
  );
}

function IncomeStream({ label, amount, color }) {
  return (
    <div className="flex items-center justify-between text-xs" data-testid={`income-stream-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-sm" style={{ backgroundColor: color }} />
        <span className="text-[#6B7280]">{label}</span>
      </div>
      <span className="font-mono font-medium text-[#1B2A4A]">{fmtFull(amount)}</span>
    </div>
  );
}

function BalanceSkeleton({ large }) {
  return (
    <div className="panel p-5">
      <Skeleton className="h-3 w-24 mb-3 bg-gray-100" />
      <Skeleton className={`${large ? 'h-9' : 'h-8'} w-40 mb-2 bg-gray-100`} />
      <Skeleton className="h-3 w-32 bg-gray-100" />
    </div>
  );
}
